import { memo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  IconButton,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TablePagination,
  Chip,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import CallReceivedIcon from "@mui/icons-material/CallReceived";
import DeleteIcon from "@mui/icons-material/Delete";
import LoadingComponent from "../components/LoadingComponent.jsx";
import TableLoader from "../components/TableLoader.jsx";
import PartnershipActions from "../components/PartnershipActions.jsx";
import { useAuth } from "../hooks/useAuth.jsx";
import { usePartnerships } from "../hooks/usePartnerships.jsx";

const Partnership = () => {
  const navigate = useNavigate();
  const {
    partnerships,
    availablePartnerships,
    loading,
    error,
    pagination,
    filters,
    partnershipsCount,
    handleChangePage,
    handleChangeRowsPerPage,
    createPartnership,
    deletePartnership,
    refetchPartnerships,
    refetchAvaliablePartnerships,
  } = usePartnerships();
  const { user } = useAuth();
  const [selectedPartnership, setSelectedPartnership] = useState(null);

  const handleTablePageChange = (event, newPage) => {
    handleChangePage(newPage + 1);
  };

  const handleRowsPerPageChange = (event) => {
    handleChangeRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleDelete = async (partnershipId) => {
    if (window.confirm("Estas seguro que quieres eliminar esta alianza?")) {
      await deletePartnership(partnershipId);
    }
  };

  return (
    <>
      {loading && <LoadingComponent />}
      {error && (
        <Alert sx={{ borderRadius: "0px !important" }} severity="error">
          {error}
        </Alert>
      )}
      <Grid item xs={12} className="mg-t-20">
        <h2>Mis Alianzas de Despacho</h2>
      </Grid>

      <PartnershipActions
        loading={loading}
        error={error}
        createPartnership={createPartnership}
        availablePartnerships={availablePartnerships}
      />

      <Grid container>
        <Grid container className="custom-table-container">
          <Grid item xs={12} className="flex-center mg-t-0">
            <>
              <TableContainer>
                {loading ? (
                  <TableLoader />
                ) : (
                  <>
                    <Table
                      aria-label="tabla de Alianzas de Despacho"
                      className="fixed-table"
                    >
                      <TableHead sx={{ backgroundColor: "#fff" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>Rol</TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Partner
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Email Contacto
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Capacidad Máxima
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }} align="right">
                            Acciones
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {partnerships.map((partnership) => {
                          const isSource = partnership.role === "SOURCE";
                          const partnerData = isSource
                            ? partnership.target_admin_details
                            : partnership.source_admin_details;
                          return (
                            <TableRow
                              key={partnership.id}
                              hover
                              sx={{
                                "& > *": { borderBottom: "unset" },
                                backgroundColor: "#fff",
                              }}
                            >
                              <TableCell>
                                {isSource ? (
                                  <Chip
                                    icon={<ArrowOutwardIcon />}
                                    label="Saliente"
                                    color="primary"
                                    variant="outlined"
                                    size="small"
                                    title="Tú envías -> Partner Recibe"
                                  />
                                ) : (
                                  <Chip
                                    icon={<CallReceivedIcon />}
                                    label="Entrante"
                                    color="success"
                                    variant="outlined"
                                    size="small"
                                    title="Partner Envía -> Tú Recibes"
                                  />
                                )}
                              </TableCell>
                              <TableCell component="th" scope="row">
                                <Typography variant="body2" fontWeight="bold">
                                  {partnership.partner_name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                >
                                  Admin ID: {partnerData?.id}
                                </Typography>
                              </TableCell>
                              <TableCell component="th" scope="row">
                                {partnerData?.email || "-"}
                              </TableCell>
                              <TableCell component="th" scope="row">
                                {partnerData?.upselling_capacity ?? 0}
                              </TableCell>
                              <TableCell
                                component="th"
                                scope="row"
                                align="right"
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(partnership.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </>
                )}

                {!loading && partnerships.length === 0 && (
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      No se encontraron alianzas de despacho.
                    </Typography>
                  </Box>
                )}

                <TablePagination
                  rowsPerPageOptions={[20, 40, 60, 80, 100]}
                  component="div"
                  count={partnershipsCount}
                  rowsPerPage={pagination.rowsPerPage}
                  page={pagination.page - 1}
                  onPageChange={handleTablePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              </TableContainer>
            </>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default memo(Partnership);
